import entries from "lodash/entries";

import { definedMap, hasOwnProperty } from "collection-utils";

import type {
    Description,
    InputOutputTables,
    MutatingScreenKind,
    TableColumn,
    PropertyDescription,
} from "../../common/description";
import { PropertyKind, isRelationType, arePropertySourcesEqual } from "../../common/description";
import type { PopulationMode } from "./description-handlers";
import { handlerForPropertyKind } from "./description-handlers";
import type {
    RewritingComponentConfiguratorContext,
    AppDescriptionContext,
    PropertyDescriptorCase,
    PropertyDescriptor,
    EnumPropertyDescriptorCase,
} from ".";
import { getPropertyDescription, isMultiCasePropertyDescriptor, updatesForProperty } from ".";
import { truthify, defined, assert } from "../../common/Support";
import { getSourceColumnProperty } from "./property-utils";
import { findCaptionDescriptor } from "./property-handlers";
import { getPropertyDescriptorCaseForDescription } from "../description-utils";
import { getDefaultCaption } from "./descriptor-utils";
import { getColumnForSourceColumn } from "../source-column";
import type { BasePrimitiveValue } from "../../common/components/computation-types";

function reorderForDefaultCaption(pds: readonly PropertyDescriptor[]): readonly PropertyDescriptor[] {
    const captionDescriptor = findCaptionDescriptor(pds);
    if (captionDescriptor === undefined) {
        return pds;
    } else {
        const reordered = pds.filter(pd => pd !== captionDescriptor);
        reordered.push(captionDescriptor);
        return reordered;
    }
}

export function populateDescription<T extends Description, TRewrite extends boolean>(
    getPropertyDescriptors: (desc: T) => readonly PropertyDescriptor[] | undefined,
    populationMode: PopulationMode,
    rootDesc: T,
    desc: T,
    tables: InputOutputTables,
    ccc: TRewrite extends true ? RewritingComponentConfiguratorContext : AppDescriptionContext,
    isRewrite: TRewrite,
    mutatingScreenKind: MutatingScreenKind | undefined,
    usedColumns?: ReadonlySet<TableColumn>,
    editedColumns?: ReadonlySet<TableColumn>,
    ignoreRequired: boolean = false
): TRewrite extends true ? T : T | undefined {
    const isRootDesc = desc === rootDesc;

    function failure(): TRewrite extends true ? T : T | undefined {
        assert(!isRewrite);
        return undefined as TRewrite extends true ? T : T | undefined;
    }

    let pds = definedMap(getPropertyDescriptors(desc), reorderForDefaultCaption);
    if (pds === undefined) {
        if (!isRewrite) return failure();
        pds = [];
    }

    let propertyDescriptorQueue: PropertyDescriptor[] = Array.from(pds);

    function updatePropertyDescriptors(
        descUpdate: Partial<Description>,
        currentDescriptor: PropertyDescriptor,
        pdc: PropertyDescriptorCase
    ): boolean {
        let needUpdate = false;

        if (
            pdc.kind === PropertyKind.Enum &&
            truthify((pdc as EnumPropertyDescriptorCase<BasePrimitiveValue>).changesDescriptor)
        ) {
            needUpdate = true;
        } else {
            for (const [, pd] of entries(descUpdate)) {
                if (!hasOwnProperty(pd, "kind")) continue;

                if (pd.kind === PropertyKind.Column) {
                    const sourceColumn = getSourceColumnProperty(pd as PropertyDescription);
                    if (sourceColumn === undefined) continue;

                    const column = getColumnForSourceColumn(ccc, sourceColumn, tables.input, undefined);
                    if (column === undefined) continue;

                    if (isRelationType(column.type)) {
                        needUpdate = true;
                        break;
                    }
                } else if (pd.kind === PropertyKind.Table) {
                    needUpdate = true;
                    break;
                }
            }
        }

        if (!needUpdate) return true;

        pds = definedMap(getPropertyDescriptors(desc), reorderForDefaultCaption);
        if (pds === undefined) {
            if (!isRewrite) return false;
            pds = [];
        }

        const index = pds.findIndex(pd => arePropertySourcesEqual(pd.property, currentDescriptor.property));
        assert(index >= 0);

        propertyDescriptorQueue = pds.slice(index + 1);

        return true;
    }

    function updateDesc(
        update: Partial<Description>,
        currentDescriptor: PropertyDescriptor,
        pdc: PropertyDescriptorCase
    ): boolean {
        desc = { ...desc, ...update };
        if (isRootDesc) {
            rootDesc = desc;
        }
        return updatePropertyDescriptors(update, currentDescriptor, pdc);
    }

    function getCaption(): string | undefined {
        return getDefaultCaption(defined(pds), desc, tables, ccc);
    }

    const avoidDirectColumns = new Set(definedMap(usedColumns, c => Array.from(c)));
    const avoidEditedColumns = new Set(definedMap(editedColumns, c => Array.from(c)));
    const avoidIndirectColumns = new Set<TableColumn>();

    for (;;) {
        const descr = propertyDescriptorQueue.shift();
        if (descr === undefined) break;

        if (isRewrite && descr.doNotRewrite === true) continue;

        let didRetry = false;
        for (;;) {
            const propertyDesc = getPropertyDescription(desc, descr);

            let pdc = definedMap(propertyDesc, d => getPropertyDescriptorCaseForDescription(d, descr));
            if (pdc === undefined) {
                if (isMultiCasePropertyDescriptor(descr)) {
                    pdc = defined(descr.cases[0]);
                } else {
                    pdc = descr;
                }
            }

            const handler = handlerForPropertyKind(pdc.kind);

            // These two are just to mollify eslint.
            const rootDescForDefault = rootDesc;
            const descForDefault = desc;
            function getDefaultUpdate() {
                return handler.defaultUpdateForPropertyCase(
                    defined(descr),
                    rootDescForDefault,
                    descForDefault,
                    defined(pdc),
                    avoidDirectColumns,
                    avoidEditedColumns,
                    avoidIndirectColumns,
                    tables,
                    mutatingScreenKind,
                    ccc,
                    populationMode,
                    getCaption
                );
            }

            if (propertyDesc === undefined || !isRewrite) {
                const update = getDefaultUpdate();

                if (update !== undefined) {
                    if (!updateDesc(update, descr, pdc)) {
                        if (isRewrite) break;
                        return failure();
                    }
                } else {
                    if (!ignoreRequired) {
                        if (isRewrite) break;
                        return failure();
                    }
                    if (!updateDesc(updatesForProperty<any>(descr.property, desc, undefined, undefined), descr, pdc)) {
                        return failure();
                    }
                }
            } else if (isRewrite) {
                let update = handler.rewriteWithCase(
                    descr,
                    pdc,
                    propertyDesc,
                    rootDesc,
                    desc,
                    tables,
                    mutatingScreenKind,
                    ignoreRequired,
                    ccc as RewritingComponentConfiguratorContext
                );
                if (update === undefined) {
                    update = getDefaultUpdate();
                    if (update === undefined) break;
                }

                if (!updateDesc(update, descr, pdc)) {
                    return failure();
                }

                if (!didRetry && getPropertyDescription(desc, descr) === undefined) {
                    didRetry = true;
                    continue;
                }
            } else {
                return failure();
            }
            break;
        }
    }

    return desc as TRewrite extends true ? T : T | undefined;
}
